import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Flex, Grid, Text, Select, Input, InputGroup, InputLeftElement, Icon, IconButton, Spinner, Button,
} from '@chakra-ui/react';
import { FiSearch, FiChevronDown, FiChevronRight, FiSliders } from 'react-icons/fi';
import { getActivation, ACTIVATION_STATE, formatScore } from 'api/administrador/monitoring/aiGrading';
import AiPagination from './AiPagination';
import { TabIntro } from './common';
import ActivationModal from './ActivationModal';

const COLS = '2.4fr 1.4fr 0.9fr 1fr 1fr 1fr';

function StatePill({ state }) {
  const s = ACTIVATION_STATE[state] || ACTIVATION_STATE.off;
  const tones = { green: ['#ECFDF5', '#047857', '#A7F3D0'], blue: ['#EEF0FE', '#3730A3', '#C7D2FE'], gray: ['#F1F5F9', '#475569', '#E2E8F0'] };
  const [bg, color, border] = tones[s.tone];
  return (
    <Flex as="span" align="center" gap={1.5} justifySelf="start" px={2.5} py={1} borderRadius="999px" fontSize="12px" fontWeight="600" bg={bg} color={color} border={`1px solid ${border}`}>
      <Box w="6px" h="6px" borderRadius="999px" bg={s.dot} />{s.label}
    </Flex>
  );
}

function Row({ row, isProgram, open, onToggle, indent }) {
  const pct = row.activities.total ? (row.activities.on / row.activities.total) * 100 : 0;
  const sub = isProgram ? row.type : indent ? null : `${row.program?.name}${row.program?.type ? ` · ${row.program.type}` : ''}`;
  return (
    <Grid templateColumns={COLS} px={5} py={3} alignItems="center" borderTop="1px solid" borderColor="gray.100" bg={isProgram ? '#FAFAFC' : 'white'}>
      <Flex align="center" gap={2} pl={indent ? '30px' : 0} minW={0}>
        {isProgram && (
          <IconButton
            aria-label={`${open ? 'Ocultar' : 'Ver'} materias de ${row.name}`}
            aria-expanded={open}
            icon={open ? <FiChevronDown /> : <FiChevronRight />}
            size="xs" variant="ghost" ml="-6px" onClick={onToggle}
          />
        )}
        <Box minW={0}>
          <Text fontSize="sm" fontWeight={isProgram ? 700 : indent ? 400 : 600} isTruncated>{row.name}</Text>
          {sub && <Text fontSize="xs" color="gray.500" isTruncated>{sub}</Text>}
        </Box>
        {row.newOff > 0 && (
          <Box as="span" px={2} py="2px" borderRadius="999px" bg="#FFFBEB" border="1px solid #FDE68A" color="#B45309" fontSize="11px" fontWeight="600" whiteSpace="nowrap">
            {row.newOff === 1 ? '1 nueva sin activar' : `${row.newOff} nuevas sin activar`}
          </Box>
        )}
      </Flex>
      <Flex align="center" gap={2.5}>
        <Box w="90px" h="6px" borderRadius="999px" bg="gray.100"><Box h="6px" w={`${pct}%`} borderRadius="999px" bg="#574feb" /></Box>
        <Text fontSize="13px" color="gray.700">{row.activities.on} de {row.activities.total}</Text>
      </Flex>
      <Text fontSize="sm">{row.sittingsWeek}</Text>
      <Text fontSize="sm">{row.aiShare === null ? '—' : `${row.aiShare} %`}</Text>
      <Text fontSize="sm">{row.aiAvg === null ? '—' : formatScore(row.aiAvg)} / {row.teacherAvg === null ? '—' : formatScore(row.teacherAvg)}</Text>
      <StatePill state={row.state} />
    </Grid>
  );
}

export default function ActivationTab({ tick }) {
  const [filters, setFilters] = useState({ view: 'programs', status: '', search: '', page: 1 });
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchText ? f : { ...f, search: searchText, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      setData(await getActivation({ ...filters, pageSize: 20 }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[calificación] error cargando la activación:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => { if (tick) load(true); }, [tick]);

  const c = data?.coverage;
  const coveragePct = c?.sittingsWeek ? Math.round((c.sittingsWeekOn / c.sittingsWeek) * 100) : 0;
  const isP = filters.view === 'programs';
  const rows = data?.data || [];

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
      <TabIntro title="Activación">
        Elige qué programas y materias se califican automáticamente. Activar marca todas sus actividades de entrega:
        desde ese momento los estudiantes envían con revisión previa y sus notas se publican solas. Lo que ya estaba
        enviado sigue con el docente.
      </TabIntro>
      <Flex px={5} pt={4} pb={2} gap={6} justify="space-between" align="center" wrap="wrap">
        <Box>
          <Text fontSize="15px" fontWeight="600" color="gray.800">
            {c ? `${c.activitiesOn.toLocaleString('es-CO')} de ${c.activities.toLocaleString('es-CO')} actividades califican automáticamente` : 'Cargando…'}
          </Text>
          <Flex align="center" gap={3} mt={2}>
            <Box w="260px" h="8px" borderRadius="999px" bg="gray.100"><Box h="8px" w={`${coveragePct}%`} borderRadius="999px" bg="#574feb" /></Box>
            <Text fontSize="13px" color="gray.600">{coveragePct}{'\u00a0'}% de las entregas de la última semana ·{' '}
              <Text as="span" whiteSpace="nowrap">meta 80–90 %</Text>
            </Text>
          </Flex>
        </Box>
        <Button
          leftIcon={<Icon as={FiSliders} />}
          onClick={() => setModalOpen(true)}
          bg="#574feb" color="white" _hover={{ bg: '#4338CA' }} borderRadius="10px" h="42px"
        >
          Activar / desactivar
        </Button>
      </Flex>

      <Flex px={5} py={4} gap={3} align="center" wrap="wrap">
        <Flex as="label" align="center" gap={2} fontSize="13px" color="gray.600">
          Ver por
          <Select size="md" w="150px" borderRadius="10px" value={filters.view} onChange={(e) => { setSearchText(''); setFilters({ ...filters, view: e.target.value, search: '', page: 1 }); }}>
            <option value="programs">Programas</option>
            <option value="subjects">Materias</option>
          </Select>
        </Flex>
        <InputGroup w={{ base: '100%', md: '320px' }}>
          <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
          <Input
            aria-label={isP ? 'Buscar programa' : 'Buscar materia'}
            placeholder={isP ? 'Buscar programa…' : 'Buscar materia…'}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            borderRadius="10px"
          />
        </InputGroup>
        <Flex as="label" align="center" gap={2} fontSize="13px" color="gray.600">
          Estado
          <Select size="md" w="140px" borderRadius="10px" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}>
            <option value="">Todas</option>
            <option value="on">Activas</option>
            <option value="mixed">Parciales</option>
            <option value="off">Inactivas</option>
          </Select>
        </Flex>
        <Text ml="auto" fontSize="13px" color="gray.500">
          {data ? `${data.pagination.total} ${isP ? 'programas' : 'materias'}` : ''}
        </Text>
      </Flex>

      <Grid templateColumns={COLS} px={5} py={3} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase">
        <Text>{isP ? 'Programa › Materia' : 'Materia · Programa'}</Text><Text>Actividades con IA</Text><Text>Entregas 7 d</Text>
        <Text>Calificadas IA</Text><Text>Nota IA / doc.</Text><Text>Estado</Text>
      </Grid>

      {loading && !rows.length && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}
      {!loading && !rows.length && <Text textAlign="center" py={10} color="gray.500" fontSize="sm">No hay resultados con esos filtros.</Text>}

      {isP
        ? rows.map((p) => (
          <Box key={p.key}>
            <Row row={p} isProgram open={!!open[p.key]} onToggle={() => setOpen({ ...open, [p.key]: !open[p.key] })} />
            {open[p.key] && p.subjects.map((s) => <Row key={s.id} row={s} indent />)}
          </Box>
        ))
        : rows.map((s) => <Row key={s.id} row={s} />)}

      <AiPagination
        pagination={data?.pagination}
        noun={isP ? 'programas' : 'materias'}
        onPageChange={(page) => setFilters({ ...filters, page })}
      />

      <ActivationModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onDone={() => load(false)} />
    </Box>
  );
}
